import * as React from "react";
import {Song} from "../../../../store/api/types";
import styles from './PlaylistItem.module.scss';
import ListItem from "@mui/material/ListItem";
import {ListItemButton, Tooltip} from "@mui/material";
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import IconButton from "@mui/material/IconButton";
import {useDispatch} from "react-redux";
import {PlaylistPickerDialog} from "../../../library/PlaylistPickerDialog";
import {useBreakpoint} from "../../../../hooks/useBreakpoint";
import {toggleLike} from "../../../../store/slices/likedSongs/reducers";
import {useIsLiked} from "../../../../store/slices/likedSongs/likedSongsSlice";

interface ItemActionsProps {
    song: Song;
    onRemove?: () => void;
    sourceImg?: string;
}

const ItemActions: React.FC<ItemActionsProps> = ({song, onRemove, sourceImg}) => {
    const dispatch = useDispatch();
    const [pickerOpen, setPickerOpen] = React.useState(false);
    const isLiked = useIsLiked(song.id);
    const isDesktop = useBreakpoint() === 'desktop';

    const stop = (e: React.MouseEvent) => e.stopPropagation();
    const handleLike = (e: React.MouseEvent) => { stop(e); dispatch(toggleLike(song)); };
    const handleAdd = (e: React.MouseEvent) => { stop(e); setPickerOpen(true); };
    const handleRemove = (e: React.MouseEvent) => { stop(e); onRemove?.(); };

    return (
        <>
            <div className={styles.actions} data-desktop={isDesktop || undefined}>
                <Tooltip title={isLiked ? 'Remove from Liked Songs' : 'Add to Liked Songs'}>
                    <IconButton
                        aria-label={isLiked ? 'Remove from Liked Songs' : 'Add to Liked Songs'}
                        onClick={handleLike}
                        className={isLiked ? styles.actions__likeOn : undefined}
                    >
                        {isLiked ? <FavoriteIcon fontSize="small"/> : <FavoriteBorderIcon fontSize="small"/>}
                    </IconButton>
                </Tooltip>
                <Tooltip title="Add to Playlist">
                    <IconButton aria-label="Add to Playlist" onClick={handleAdd}>
                        <PlaylistAddIcon fontSize="small"/>
                    </IconButton>
                </Tooltip>
                {onRemove && (
                    <Tooltip title="Remove from Playlist">
                        <IconButton aria-label="Remove from Playlist" onClick={handleRemove}>
                            <DeleteOutlineIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                )}
            </div>
            <PlaylistPickerDialog
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                song={song}
                sourceImg={sourceImg}
            />
        </>
    );
};

export interface PlaylistItemProps {
    item: Song;
    index: number;
    selected?: boolean;
    onClick?: (song: Song) => void;
    onRemove?: () => void;
    sourceImg?: string;
    dragHandle?: React.ReactNode;
}

export const PlaylistItem: React.FC<PlaylistItemProps> = ({item, index, selected, onClick, onRemove, sourceImg, dragHandle}) => {
    const handleClick = () => {
        onClick?.(item);
    };

    return (
        <ListItem className={`${styles.playlistItem__container} ${selected ? styles.playlistItem__selected : ''}`}>
            {dragHandle}
            <ListItemButton role="button" onClick={handleClick}>
                <div className={styles.playlistItem__index}>{index}</div>
                <div className={styles.playlistItem__info}>
                    <div className={styles.playlistItem__title}>{item.name}</div>
                    <div className={styles.playlistItem__artist}>{item.artist}</div>
                </div>
            </ListItemButton>
            <ItemActions song={item} onRemove={onRemove} sourceImg={sourceImg}/>
        </ListItem>
    );
};
